import type { SessionRepairStatus } from '../../session-repair/types';
import { SESSION_REPAIR_STATUS_LABELS } from '../../session-repair/constants';
import type { SpectralWorkspaceSession } from '../types';

export type WorkspaceSessionRow = SpectralWorkspaceSession & {
  hrEventCount: number;
};

export type WorkspaceSessionsSort = {
  columnId: 'sessionId' | 'patientCode' | 'repairStatus' | 'hrEventCount';
  desc: boolean;
};

const REPAIR_STATUS_SORT_ORDER: Record<SessionRepairStatus, number> = {
  no_hr_data: 0,
  analysis_failed: 1,
  not_analyzed: 2,
  analyzing: 3,
  needs_repair: 4,
  perfect: 5,
  repaired_perfect: 6,
};

export const buildWorkspaceSessionRows = (
  sessions: SpectralWorkspaceSession[]
): WorkspaceSessionRow[] =>
  sessions.map(session => ({
    ...session,
    hrEventCount:
      session.status === 'ready' && session.events != null
        ? session.events.length
        : -1,
  }));

export const comparePatientCodes = (
  left: string | null | undefined,
  right: string | null | undefined
) => {
  const leftMissing = !left?.trim();
  const rightMissing = !right?.trim();

  if (leftMissing && rightMissing) {
    return 0;
  }

  if (leftMissing) {
    return 1;
  }

  if (rightMissing) {
    return -1;
  }

  return (left as string).localeCompare(right as string, 'it', {
    sensitivity: 'base',
  });
};

export const compareRepairStatus = (
  left: SessionRepairStatus | undefined,
  right: SessionRepairStatus | undefined
) => {
  const leftOrder = left ? REPAIR_STATUS_SORT_ORDER[left] : -1;
  const rightOrder = right ? REPAIR_STATUS_SORT_ORDER[right] : -1;

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  const leftLabel = left ? SESSION_REPAIR_STATUS_LABELS[left] : '';
  const rightLabel = right ? SESSION_REPAIR_STATUS_LABELS[right] : '';

  return leftLabel.localeCompare(rightLabel, 'it', { sensitivity: 'base' });
};

export const compareHrEventCount = (left: number, right: number) =>
  left - right;

const compareRows = (
  left: WorkspaceSessionRow,
  right: WorkspaceSessionRow,
  sort: WorkspaceSessionsSort
) => {
  const direction = sort.desc ? -1 : 1;

  switch (sort.columnId) {
    case 'patientCode':
      return (
        comparePatientCodes(left.patientCode, right.patientCode) * direction
      );
    case 'repairStatus':
      return (
        compareRepairStatus(left.repairStatus, right.repairStatus) * direction
      );
    case 'hrEventCount':
      return (
        compareHrEventCount(left.hrEventCount, right.hrEventCount) * direction
      );
    case 'sessionId':
    default:
      return compareHrEventCount(left.sessionId, right.sessionId) * direction;
  }
};

export const sortWorkspaceSessions = (
  sessions: SpectralWorkspaceSession[],
  sort: WorkspaceSessionsSort
): WorkspaceSessionRow[] => {
  const rows = buildWorkspaceSessionRows(sessions);

  return [...rows].sort((left, right) => {
    const result = compareRows(left, right, sort);

    if (result !== 0) {
      return result;
    }

    return left.sessionId - right.sessionId;
  });
};

export const DEFAULT_WORKSPACE_SESSIONS_SORT: WorkspaceSessionsSort = {
  columnId: 'sessionId',
  desc: false,
};
